import { clsx } from 'clsx';
import { ErrorRounded } from '@material-ui/icons';

import { ToppingsByParentId, TotalsSection } from './components';
import { ReactComponent as TurboIcon } from './assets/turbo-icon.svg';
import { ReactComponent as RappiLogo } from 'assets/images/logos/rappi_logo_to_print.svg';

import { useOrderInvoicePrinting } from './hooks';
import { formatCurrency } from 'utils/CurrencyUtils';

import './printPage.css';
import makeClasses from './OrderInvoicePrinting.style';

import type { OrderIdForcedProps } from './domain';

export function OrderInvoicePrinting(props: OrderIdForcedProps) {
  const { orderIdForced = NaN } = props;

  const {
    isRenderForced,
    lang,
    localeLang,
    order,
    orderId,
    totals,
    isTurbo,
    hide,
  } = useOrderInvoicePrinting({ orderIdForced });

  const _classes = makeClasses({
    isRenderForced,
    qrCode: order.handshake.qrCode,
    hideQRCode: hide.handshake.qrCode,
    hideCommand: hide.commandBase,
    hideChecklist: hide.checklistSection,
    hasHandshake: !hide.handshake.section,
  });

  return (
    <article
      aria-label='order-invoice-printing'
      data-order-id={orderId}
      className={clsx(_classes.invoice, _classes.renderForced)}
    >
      <header className={_classes.header}>
        <figure className={_classes.iconsContainer}>
          <RappiLogo className={_classes.rappiLogoContainer} />
          {isTurbo && <TurboIcon className={_classes.turboIconContainer} />}
        </figure>
        <div className={_classes.containerClient}>
          <h1 aria-label='user-name' className={_classes.userName}>
            {order.user.name.basic}
          </h1>
          <h2
            aria-label='user-type'
            hidden={hide.clientTag}
            className={_classes.clientTag}
          >
            {`* ${lang.invoice.user.type} *`}
          </h2>
        </div>
        <h2
          aria-label='order-id'
          className={_classes.orderId}
          dangerouslySetInnerHTML={{ __html: lang.invoice.orderIdTitle }}
        />
        <div className={_classes.moreInfoOrder}>
          <p lang={localeLang} aria-label='is-pickup' hidden={hide.isPickup}>
            {lang.invoice.info.isPickup}
          </p>
          <p aria-label='store-name' lang={localeLang}>
            {lang.invoice.info.storeName}
          </p>
          <p aria-label='platform'>{lang.invoice.info.platform}</p>
          <p aria-label='created-at' className={_classes.twoPartsToFlexWrap}>
            <span>{lang.invoice.info.createdAt}</span>
            <span>{order.createdAt.replace('T', ' ').slice(0, -3)}</span>
          </p>
          <p
            lang={localeLang}
            aria-label='user-phone'
            hidden={hide.contact.phone}
          >
            {lang.invoice.info.contact.phone}
          </p>
          <p
            lang={localeLang}
            aria-label='user-address'
            hidden={hide.contact.address}
          >
            {lang.invoice.info.contact.address}
          </p>
          <p
            lang={localeLang}
            aria-label='user-zip-code'
            hidden={hide.contact.zipCode}
          >
            {lang.invoice.info.contact.zipCode}
          </p>
          <p
            lang={localeLang}
            aria-label='user-neighborhood'
            hidden={hide.contact.neighborhood}
          >
            {lang.invoice.info.contact.neighborhood}
          </p>
          <p
            lang={localeLang}
            aria-label='shipping-prime'
            hidden={hide.shippingPrime}
          >
            {lang.invoice.info.shippingPrime}
          </p>
          <p
            lang={localeLang}
            aria-label='payment-method'
            hidden={hide.paymentMethod}
          >
            {lang.invoice.info.paymentMethod}
          </p>
        </div>
      </header>

      <main aria-label='main-section or command-base' className={_classes.main}>
        <section aria-label='products-section'>
          <h1 className={_classes.titleOfDetails}>
            <span>{lang.invoice.products.title.p1}</span>
            <span>{lang.invoice.products.title.p2}</span>
          </h1>
          <ul aria-label='product-list' className={_classes.productList}>
            {order.products.map(product => (
              <li
                key={product.orderProductID}
                data-product-id={product.orderProductID}
                className={_classes.product}
              >
                <div className={_classes.productDescription}>
                  <p>{product.units}x</p>
                  <p lang={localeLang}>{product.name}</p>
                  <p>{formatCurrency(product.price.totalProduct)}</p>
                </div>
                <ToppingsByParentId
                  localeLang={localeLang}
                  toppings={product.toppings}
                />
                <p
                  lang={localeLang}
                  aria-label='comment'
                  className={_classes.comment}
                  hidden={!Boolean(product.comments)}
                >
                  {product.replace.suggestion
                    ? lang.invoice.products.item.replace.suggestion
                        .replace('_productName_', product.name)
                        .replace('_suggestion_', product.replace.suggestion)
                    : lang.invoice.products.item.comment + product.comments}
                </p>
                <p
                  lang={localeLang}
                  aria-label='global-offer'
                  className={_classes.globalOffer}
                  hidden={product.globalOffer.condition.tagPromo === ''}
                >
                  {lang.invoice.products.item.globalOffer}
                  {product.globalOffer.condition.tagPromo}.
                </p>
              </li>
            ))}
          </ul>
          {order.whims.length > 0 && (
            <aside aria-label='whims-section' className={_classes.whimsSection}>
              <h3 className={_classes.whimsTitle}>
                {lang.invoice.whims.title}
              </h3>
              {order.whims.map(whim => (
                <div
                  data-whim-id={whim.id}
                  key={`whim-${whim.id}`}
                  className={_classes.whim}
                >
                  <p lang={localeLang}>{whim.name}</p>
                  <p>{formatCurrency(whim.price)}</p>
                </div>
              ))}
            </aside>
          )}
        </section>
        <TotalsSection
          titleMsgError={lang.invoice.totals.error.msg}
          titleErrorProps={{ lang: localeLang, hidden: hide.totals.error }}
          mainContainerProps={{ hidden: hide.totals.breakdown }}
          totals={totals}
        >
          <aside
            aria-label='totals-notes'
            hidden={hide.totals.notes.aside}
            className={_classes.totalsNotes}
          >
            <p
              lang={localeLang}
              aria-label='note-cash-received-from-user'
              hidden={hide.totals.notes.cash.receivedFromUser}
            >
              {lang.invoice.totals.notes.cash.receivedFromUser}
            </p>
            <p
              lang={localeLang}
              className='bold'
              aria-label='note-cash-delivered-to-user'
              hidden={hide.totals.notes.cash.deliveredToUser}
            >
              {lang.invoice.totals.notes.cash.deliveredToUser}
            </p>
          </aside>
        </TotalsSection>
      </main>

      <footer>
        <section
          aria-label='qrcode-section'
          className={_classes.handshakeSection}
          hidden={hide.handshake.section}
        >
          <h1 className={_classes.handshakeTitle}>
            {lang.invoice.handshake.title}
          </h1>
          <p className={_classes.handshakeDescription}>
            {lang.invoice.handshake.description}
          </p>
          <figure aria-label='qrcode-figure'>
            {/* load QR img with src is not print all times -> use div */}
            {/*<img*/}
            {/*  src={qrCode}*/}
            {/*  alt={undefined}*/}
            {/*  decoding='sync'*/}
            {/*  className={_classes.qrCode}*/}
            {/*/>*/}
            <div className={_classes.handshakeCodeContainerFix}>
              <div role='img' aria-label='qrcode' className={_classes.qrCode} />
            </div>
            <figcaption
              hidden={hide.handshake.manualCode}
              className={_classes.manualCode}
            >
              {lang.invoice.handshake.manualCode}
            </figcaption>
          </figure>
        </section>

        <aside
          aria-label='checklist-section'
          className={_classes.checklistSection}
        >
          <header className={_classes.checklistHeader}>
            <p aria-label='user-name'>{order.user.name.basic}</p>
            <p
              aria-label='order-id'
              style={{ display: 'inline-flex', gap: '0.25rem' }}
              dangerouslySetInnerHTML={{ __html: lang.invoice.orderIdTitle }}
            />
            <p aria-label='store-name' lang={localeLang}>
              {lang.checklist.header.storeName}
            </p>
          </header>
          <h1 className={_classes.checklistTitle}>
            <span>{lang.checklist.title.p1}</span>
            <span>{lang.checklist.title.p2}</span>
          </h1>
          <ul aria-label='checklist' className={_classes.checklist}>
            {order.products.map(product => (
              <li
                key={`${product.orderProductID}-checklist`}
                data-product-id={`${product.orderProductID}-checklist-item`}
              >
                <header className={_classes.checklistItemDescription}>
                  <p>&#x25A1;&#x00A0;{product.units}x</p>
                  <p lang={localeLang}>{product.name}</p>
                </header>
                {product.report.defectRate.enabled && (
                  <p
                    role='alert'
                    aria-label='checklist-defect-rate-report'
                    className={_classes.checklistDefectRate}
                  >
                    <ErrorRounded style={{ fontSize: '0.875rem' }} />
                    <span lang={localeLang}>
                      {lang.checklist.item.report.defectRate}
                    </span>
                  </p>
                )}
                {/*
                  toppings by a category removed by RA-4555, and they come back
                  through HU-4057 but as a list without nesting or categories.
                */}
                <ul className={_classes.checklistToppings}>
                  {product.toppings.map((topping, tIdx) => (
                    <li
                      lang={localeLang}
                      data-has-price={topping.price.real > 0}
                      key={`${topping.id}-checklist-${tIdx + 1}`}
                      data-topping-id={`${topping.id}-checklist-${tIdx + 1}`}
                    >
                      {topping.units > 1 && `${topping.units}x `}
                      {topping.description}
                    </li>
                  ))}
                </ul>
                <p
                  lang={localeLang}
                  aria-label='comment'
                  hidden={!Boolean(product.comments)}
                  className={_classes.checklistComment}
                >
                  <span className='bold'>
                    &#x25A1;&#x00A0;{lang.checklist.item.comment}
                  </span>
                  {product.comments}
                </p>
              </li>
            ))}
          </ul>
        </aside>
      </footer>
    </article>
  );
}
